import { useCallback, useEffect, useRef, useState } from "react";
import type { TaskEditorState } from "../utils/taskEditorUtils";
import { resumeTaskAuthoring, streamTaskAuthoring } from "./apiClient";
import { clearChat, loadChat, saveChat } from "./chatStorage";
import type {
  AssistantStep,
  AssistantTurn,
  ChatTurn,
  Decision,
  SSEEvent,
  TaskBlocksPatch,
  UserTurn,
} from "./types";

// 이벤트가 한 건도 안 오는 서버 hang 을 끊는 idle 타임아웃.
const IDLE_TIMEOUT_MS = 60_000;
// final/error 수신 후 trailing debug 이벤트를 잠깐 기다렸다가 연결을 정리하는 grace.
const POST_FINAL_GRACE_MS = 2_000;

const rid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

const generateThreadId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return rid() + "-" + rid();
};

interface UseAgentChatThreadResult {
  threadId: string | null;
  turns: ChatTurn[];
  isStreaming: boolean;
  send: (message: string, currentTask?: TaskEditorState) => Promise<void>;
  respondToApproval: (turnId: string, decisions: Decision[]) => Promise<void>;
  clear: () => void;
  cancel: () => void;
}

export function useAgentChatThread(
  taskId: string | undefined,
  onTaskPatch: (patch: TaskBlocksPatch) => void
): UseAgentChatThreadResult {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const onTaskPatchRef = useRef(onTaskPatch);

  useEffect(() => {
    onTaskPatchRef.current = onTaskPatch;
  }, [onTaskPatch]);

  // taskId 변경 시 storage 로드
  useEffect(() => {
    if (!taskId) {
      setThreadId(null);
      setTurns([]);
      return;
    }
    const loaded = loadChat(taskId);
    setThreadId(loaded.threadId);
    setTurns(loaded.turns);
  }, [taskId]);

  // turns/threadId 변경 시 storage 저장
  useEffect(() => {
    if (!taskId) return;
    saveChat(taskId, { threadId, turns });
  }, [taskId, threadId, turns]);

  // assistantTurn 갱신 reducer
  const applyEvent = useCallback((turnId: string, event: SSEEvent) => {
    setTurns((prev) =>
      prev.map((turn) => {
        if (turn.role !== "assistant" || turn.id !== turnId) return turn;
        return reduceAssistantTurn(turn, event);
      })
    );
    if (event.event === "task_patch") {
      onTaskPatchRef.current(event.data.task);
    }
  }, []);

  const consumeStream = useCallback(
    async (
      turnId: string,
      stream: AsyncGenerator<SSEEvent>,
      controller: AbortController
    ) => {
      const signal = controller.signal;
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      let timedOut = false;
      let finalized = false;

      // 이벤트가 올 때마다 idle 타이머 리셋. 만료되면 turn 을 timeout 에러로
      // 표시하고 연결을 끊는다. (abort 시 제너레이터는 throw 없이 정상 종료되므로
      // 에러 표시는 여기서 직접 한다 — catch 에 의존하지 않음.)
      const armIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          timedOut = true;
          setTurns((prev) =>
            prev.map((turn) =>
              turn.role === "assistant" &&
              turn.id === turnId &&
              turn.status === "streaming"
                ? {
                    ...turn,
                    status: "error",
                    error: {
                      type: "timeout",
                      message: "응답 시간이 초과됐어요. 다시 시도해 주세요.",
                    },
                  }
                : turn
            )
          );
          setIsStreaming(false);
          controller.abort();
        }, IDLE_TIMEOUT_MS);
      };

      try {
        armIdle();
        for await (const event of stream) {
          if (signal.aborted) break;
          // finalized 후엔 grace 타이머가 연결 정리를 담당하므로 idle 재무장 안 함.
          if (!finalized) armIdle();
          applyEvent(turnId, event);
          if (event.event === "final" || event.event === "error") {
            // 논리적 종료 — 스피너 즉시 해제. 연결은 trailing debug 를 위해
            // 짧은 grace 뒤 정리한다 (서버가 close 안 해도 무한 대기 방지).
            finalized = true;
            setIsStreaming(false);
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(
              () => controller.abort(),
              POST_FINAL_GRACE_MS
            );
          }
        }
      } catch (err) {
        // abort(타임아웃/사용자취소/grace)·정상종료는 throw 하지 않는다.
        // 여기 오는 건 진짜 네트워크/스트림 오류뿐. timeout 은 armIdle 에서 이미 처리.
        if (!finalized && !timedOut && !signal.aborted) {
          const message = err instanceof Error ? err.message : String(err);
          setTurns((prev) =>
            prev.map((turn) =>
              turn.role === "assistant" && turn.id === turnId
                ? {
                    ...turn,
                    status: "error",
                    error: { type: "stream_error", message },
                  }
                : turn
            )
          );
        }
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
        // 잔여 연결이 정리되는 사이 새 turn 이 시작됐다면 그쪽 스피너를 끄지 않는다.
        if (abortRef.current === controller) setIsStreaming(false);
      }
    },
    [applyEvent]
  );

  const send = useCallback(
    async (message: string, currentTask?: TaskEditorState) => {
      if (isStreaming) return;
      let tid = threadId;
      if (!tid) {
        tid = generateThreadId();
        setThreadId(tid);
      }
      const userTurn: UserTurn = {
        id: rid(),
        role: "user",
        content: message,
        createdAt: Date.now(),
      };
      const assistantTurn: AssistantTurn = {
        id: rid(),
        role: "assistant",
        steps: [],
        status: "streaming",
        taskChanged: false,
        createdAt: Date.now(),
      };
      setTurns((prev) => [...prev, userTurn, assistantTurn]);
      setIsStreaming(true);

      // 직전 turn 의 잔여 연결(서버가 close 안 한 경우)을 정리하고 시작.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const stream = streamTaskAuthoring(
        {
          thread_id: tid,
          message,
          current_task: currentTask ?? null,
        },
        controller.signal
      );
      await consumeStream(assistantTurn.id, stream, controller);
    },
    [consumeStream, isStreaming, threadId]
  );

  const respondToApproval = useCallback(
    async (turnId: string, decisions: Decision[]) => {
      if (isStreaming || !threadId) return;
      // 결정 step 추가 + status streaming 으로 변경
      setTurns((prev) =>
        prev.map((turn) => {
          if (turn.role !== "assistant" || turn.id !== turnId) return turn;
          if (!turn.pendingApproval) return turn;
          const actions = turn.pendingApproval.action_requests;
          const decisionSteps: AssistantStep[] = decisions.map(
            (decision, idx) => ({
              kind: "approval_decision",
              id: rid(),
              decision,
              originalAction: actions[idx] ?? actions[0],
            })
          );
          return {
            ...turn,
            steps: [...turn.steps, ...decisionSteps],
            status: "streaming",
            pendingApproval: undefined,
          };
        })
      );

      setIsStreaming(true);
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const stream = resumeTaskAuthoring(
        { thread_id: threadId, decisions },
        controller.signal
      );
      await consumeStream(turnId, stream, controller);
    },
    [consumeStream, isStreaming, threadId]
  );

  const clear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setThreadId(null);
    setTurns([]);
    setIsStreaming(false);
    if (taskId) clearChat(taskId);
  }, [taskId]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  return {
    threadId,
    turns,
    isStreaming,
    send,
    respondToApproval,
    clear,
    cancel,
  };
}

function reduceAssistantTurn(
  turn: AssistantTurn,
  event: SSEEvent
): AssistantTurn {
  switch (event.event) {
    case "session":
      return turn;

    case "tool_call": {
      const step: AssistantStep = {
        kind: "tool",
        id: rid(),
        name: event.data.name,
        args: event.data.args,
        startedAt: Date.now(),
        status: "running",
      };
      return { ...turn, steps: [...turn.steps, step] };
    }

    case "tool_result": {
      // 같은 name 의 "가장 먼저 시작된(running)" step 부터 done 처리 (FIFO).
      // tool_call/tool_result 에 id 가 없어 name + 순서로 짝짓는다 — 결과는 호출
      // 순서대로 온다고 가정. (LIFO 로 매칭하면 read_file 을 여러 개 동시에 호출할 때
      // 결과가 엉뚱한 행에 붙어 파일명과 내용이 어긋난다.)
      let updated = false;
      const nextSteps = turn.steps.map((step) => {
        if (
          !updated &&
          step.kind === "tool" &&
          step.name === event.data.name &&
          step.status === "running"
        ) {
          updated = true;
          return {
            ...step,
            status: "done" as const,
            result: event.data.preview,
            finishedAt: Date.now(),
          };
        }
        return step;
      });
      return { ...turn, steps: nextSteps };
    }

    case "message_chunk": {
      const last = turn.steps[turn.steps.length - 1];
      if (last?.kind === "text") {
        const merged: AssistantStep = {
          ...last,
          content: last.content + event.data.delta,
        };
        return {
          ...turn,
          steps: [...turn.steps.slice(0, -1), merged],
        };
      }
      const step: AssistantStep = {
        kind: "text",
        id: rid(),
        content: event.data.delta,
      };
      return { ...turn, steps: [...turn.steps, step] };
    }

    case "pending_approval":
      return {
        ...turn,
        status: "awaiting_approval",
        pendingApproval: event.data,
      };

    case "task_patch":
      return { ...turn, taskChanged: true };

    case "final":
      return {
        ...turn,
        status: "complete",
        taskChanged: event.data.task_changed,
      };

    case "debug":
      return {
        ...turn,
        debug: {
          trace_id: event.data.trace_id,
          execution_time_ms: event.data.execution_time_ms,
        },
      };

    case "error":
      return {
        ...turn,
        status: "error",
        error: { type: event.data.type, message: event.data.message },
      };

    default:
      return turn;
  }
}
